import type { ComponentProps } from "react";
import { useCallback, useState } from "react";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { formatDate, Table } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { KeywordsCountTable } from "./expanded-rows/KeywordsCountTable";
import { getSearchAdsTableColumns } from "./searchAdsTableColumnsConfig";
import type { ISearchAdsTable } from "./searchAdsTableTypes";
import { isPaidSearchPagesAvailable } from "app/pages/keyword-research/utils/permissions";

const DEFAULT_PAGE_SIZE = 20;
const isAllowedToSeePaid = isPaidSearchPagesAvailable();
export const SearchAdsTable = <T extends object = any>(props: ISearchAdsTable<T & { key: number }>) => {
    const {
        data = [],
        isLoading,
        visibleColumns = [],
        pageSize = DEFAULT_PAGE_SIZE,
        isError,
        handleSort,
        sortConfig,
        totalCount,
        currentPage,
        onPageChange,
        addToDashboardConfig,
        excelUrl,
        requestBodyForExcel,
        headerTooltipKey,
        getColumnsFunction = getSearchAdsTableColumns,
        websiteKeywordsState,
    } = props;
    const [expandedRowKey, setExpandedRowKey] = useState<number[]>([]);
    const dateFilter = useSubtitleFilters("date");
    const FormattedDate = formatDate(dateFilter[0]?.value?.from, dateFilter[0]?.value?.to);
    const headerTitle = `${translate("pla.and.ads.table.header.title")} ${FormattedDate}`;
    const headerTooltip = translate(headerTooltipKey);

    const toggleExpandRow = useCallback((key: number) => {
        setExpandedRowKey((prevExpandedKeys) => (prevExpandedKeys.includes(key) ? [] : [key]));
    }, []);

    const onPageChangeHandler = (newPage: number) => {
        onPageChange(newPage);
    };

    const closeExpandedRow = () => {
        setExpandedRowKey([]);
    };

    const columns = getColumnsFunction(
        visibleColumns,
        currentPage,
        pageSize,
        sortConfig,
        toggleExpandRow,
        totalCount,
        isAllowedToSeePaid,
        websiteKeywordsState,
    ) as ColumnsType<(typeof data)[number]>;

    const onSort: ComponentProps<typeof Table<(typeof data)[number]>>["onSort"] = (sorter) => {
        const isAsc = sorter.order === "ascend";
        handleSort({
            sort: sorter.columnKey as string,
            asc: isAsc,
        });
        onPageChange(1);
    };

    const noDataObject = isError
        ? {
              title: translate("keyword.research.search.ads.table.error.title"),
              subtitle: translate("keyword.research.search.ads.table.error.subtitle"),
          }
        : {
              title: translate("keyword.research.search.ads.table.no.data.title"),
              subtitle: translate("keyword.research.search.ads.table.no.data.subtitle"),
          };
    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `search ads table - download excel`);
    };
    return (
        <WidgetContainer
            title={headerTitle}
            tooltip={headerTooltip}
            subtitleFilters={[]}
            isLoading={false}
            loaderWidth={"100%"}
            loaderHeight={400}
            noData={!isLoading ? data.length === 0 : null}
            withBorder={true}
            noDataTitle={translate("common.noresults")}
            noDataSubtitle={translate("keyword.analysis.overview.page.v2.trend_over_time.noData.subtitle")}
            addToDashboardConfig={addToDashboardConfig}
            excelUrl={excelUrl}
            requestBodyForExcel={requestBodyForExcel}
            onExcelClick={handleExcelClick}
            isExcelDisabled={!swSettings.current.resources.IsExcelAllowed}
            totalCount={totalCount}
            hasPaidExcel={true}
        >
            <Table<(typeof data)[number]>
                columnConfig={columns}
                data={data}
                isLoading={isLoading}
                loaderRowCount={DEFAULT_PAGE_SIZE / 2}
                noData={noDataObject}
                size="large"
                pagination={{
                    pageSize,
                    total: totalCount ?? 0,
                    current: currentPage,
                    onChange: (_, __, newPage) => onPageChangeHandler(newPage), // Trigger handler
                }}
                sticky={false}
                expandable={{
                    // eslint-disable-next-line react/no-unstable-nested-components
                    expandedRowRender: (record: any) => (
                        <KeywordsCountTable
                            onClick={closeExpandedRow}
                            adId={record.adId}
                        />
                    ),
                    expandIcon: () => null, // Hide default expand icon
                    expandedRowKeys: expandedRowKey,
                    onExpand: (_, record) => toggleExpandRow(record.key),
                    expandIconColumnIndex: -1,
                    expandedRowClassName: (record) =>
                        expandedRowKey.length > 0 && expandedRowKey[0] === record.key ? "expanded-row-active" : "",
                }}
                onSort={onSort}
            />
        </WidgetContainer>
    );
};
